import { useState, useMemo, useCallback } from 'react';
import type { TreatmentCategory, NGAPAct } from '../../types/records';
import type { SelectionContext } from '../../lib/ngapLogicEngine';
import { detectSuggestedActs, type SuggestedAct } from '../../lib/ngapLogicEngine';
import { CLINICAL_INTENTIONS, type ClinicalIntention } from '../../lib/ngapConfig';

interface SmartNGAPPanelProps {
  categories: TreatmentCategory[];
  selectionContext: SelectionContext;
  onActSelect: (act: NGAPAct) => void;
  selectedActId?: string;
  sessionExtractions?: number;
}

export default function SmartNGAPPanel({
  categories,
  selectionContext,
  onActSelect,
  selectedActId,
  sessionExtractions = 0,
}: SmartNGAPPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showFullCatalog, setShowFullCatalog] = useState(false);

  // ── Smart suggestions from logic engine ──
  const suggestions = useMemo(
    () => detectSuggestedActs(selectionContext, categories, sessionExtractions),
    [selectionContext, categories, sessionExtractions]
  );

  const hasSelection = useMemo(() => {
    return (
      selectionContext.selectedTeeth.length > 0 ||
      Object.keys(selectionContext.selectedSurfaces).length > 0 ||
      Object.keys(selectionContext.selectedParts).length > 0
    );
  }, [selectionContext]);

  // Build clinical intention buttons with their best-match act
  const clinicalButtons = useMemo(() => {
    if (!hasSelection || suggestions.length === 0) return [];

    const buttons: { intention: ClinicalIntention; bestAct: SuggestedAct; allActs: SuggestedAct[] }[] = [];

    for (const intention of CLINICAL_INTENTIONS) {
      const matchingActs = suggestions.filter(s => intention.matchCodes.includes(s.act.code));
      if (matchingActs.length > 0) {
        // Pick the highest confidence auto/related act
        const autoAct = matchingActs.find(s => s.category === 'auto') ?? matchingActs[0];
        buttons.push({
          intention,
          bestAct: autoAct,
          allActs: matchingActs,
        });
      }
    }

    return buttons;
  }, [hasSelection, suggestions]);

  // Selection summary in French
  const selectionSummary = useMemo(() => {
    const parts: string[] = [];
    const teeth = selectionContext.selectedTeeth;
    const surfs = Object.entries(selectionContext.selectedSurfaces);
    const pparts = Object.entries(selectionContext.selectedParts);

    if (teeth.length > 0) {
      parts.push(`${teeth.length} dent${teeth.length > 1 ? 's' : ''}: ${teeth.join(', ')}`);
    }
    if (surfs.length > 0) {
      for (const [fdi, surfaces] of surfs) {
        parts.push(`#${fdi} surfaces: ${surfaces.join(', ')}`);
      }
    }
    if (pparts.length > 0) {
      for (const [fdi, p] of pparts) {
        parts.push(`#${fdi} parties: ${p.join(', ')}`);
      }
    }
    return parts.length > 0 ? parts.join(' · ') : 'Aucune sélection';
  }, [selectionContext]);

  // ── Filtered catalog search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const q = searchQuery.toLowerCase();
    return categories
      .map(cat => ({
        ...cat,
        acts: (cat.acts || []).filter(
          a =>
            a.code.toLowerCase().includes(q) ||
            a.label.toLowerCase().includes(q) ||
            a.label_en?.toLowerCase().includes(q)
        ),
      }))
      .filter(cat => (cat.acts?.length ?? 0) > 0);
  }, [categories, searchQuery]);

  const toggleCategory = useCallback((catId: string) => {
    setExpandedCategory(prev => (prev === catId ? null : catId));
  }, []);

  // ── Render a single act item ──
  const renderActItem = (act: NGAPAct, reason?: string, badge?: string) => {
    const isSelected = selectedActId === act.id;
    return (
      <button
        key={act.id}
        onClick={() => onActSelect(act)}
        className={`w-full text-left px-3 py-2 rounded-lg transition-all group ${
          isSelected
            ? 'bg-teal-100 dark:bg-teal-600/20 ring-1 ring-teal-400 dark:ring-teal-500/40'
            : 'hover:bg-muted'
        }`}
      >
        <div className="flex items-center gap-2">
          {act.icon_image ? (
            <img src={act.icon_image} alt="" className="w-4 h-4 rounded object-contain shrink-0" />
          ) : act.category_icon_image ? (
            <img src={act.category_icon_image} alt="" className="w-4 h-4 rounded object-contain shrink-0" />
          ) : (
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: act.category_color || '#64748b' }}
            />
          )}
          <span className="font-mono text-[10px] text-teal-600 dark:text-teal-400 shrink-0">{act.code}</span>
          {badge && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-300 font-medium">
              {badge}
            </span>
          )}
          <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-auto font-mono">
            ×{act.coefficient}
          </span>
        </div>
        <p className="text-[11px] text-slate-700 dark:text-slate-300 mt-0.5 leading-snug line-clamp-2 pl-3.5">
          {act.label}
        </p>
        {reason && (
          <p className="text-[10px] text-teal-600 dark:text-teal-400/70 mt-0.5 pl-3.5 italic">{reason}</p>
        )}
      </button>
    );
  };

  // ── Render a clinical intention button ──
  const renderClinicalButton = (
    intention: ClinicalIntention,
    bestAct: SuggestedAct,
    allActs: SuggestedAct[],
  ) => {
    const isSelected = selectedActId === bestAct.act.id;
    return (
      <button
        key={intention.id}
        onClick={() => onActSelect(bestAct.act)}
        className={`w-full text-left px-3 py-2.5 rounded-xl transition-all group ${
          isSelected
            ? 'bg-teal-100 dark:bg-teal-600/20 ring-1 ring-teal-400 dark:ring-teal-500/40'
            : 'bg-muted hover:bg-muted/80 border border-border hover:border-border/60'
        }`}
      >
        <div className="flex items-center gap-2.5">
          {bestAct.act.icon_image ? (
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-muted/50">
              <img src={bestAct.act.icon_image} alt="" className="w-7 h-7 object-contain" />
            </span>
          ) : bestAct.act.category_icon_image ? (
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-muted/50">
              <img src={bestAct.act.category_icon_image} alt="" className="w-7 h-7 object-contain" />
            </span>
          ) : (
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0"
              style={{ backgroundColor: intention.color + '22', color: intention.color }}
            >
              {intention.icon}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold text-slate-800 dark:text-slate-200">
                {intention.label}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
                Auto
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5 truncate">
              {intention.description}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="font-mono text-[10px] text-teal-600 dark:text-teal-400 font-semibold">{bestAct.act.code}</span>
            <span className="block text-[10px] text-slate-500">×{bestAct.act.coefficient}</span>
          </div>
        </div>
        {bestAct.reason && (
          <p className="text-[10px] text-teal-600 dark:text-teal-400/60 mt-1 pl-[38px] italic truncate">
            {bestAct.reason}
          </p>
        )}
        {allActs.length > 1 && (
          <div className="mt-1.5 pl-[38px] flex gap-1 flex-wrap">
            {allActs
              .filter(a => a.act.id !== bestAct.act.id)
              .slice(0, 2)
              .map(a => (
                <span
                  key={a.act.id}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onActSelect(a.act); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onActSelect(a.act); } }}
                  className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors cursor-pointer ${
                    selectedActId === a.act.id
                      ? 'bg-teal-100 dark:bg-teal-600/20 border-teal-400 dark:border-teal-500/40 text-teal-700 dark:text-teal-300'
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
                  }`}
                >
                  {a.act.code}
                </span>
              ))}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card text-card-foreground">
      {/* ── Header ── */}
      <div className="shrink-0 px-4 pt-4 pb-2">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-teal-600 dark:text-teal-400 mb-2">
          Actes NGAP
        </h2>

        {/* Selection context indicator */}
        {hasSelection && (
          <div className="mb-2 px-2.5 py-1.5 rounded-lg bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700/30">
            <div className="text-[10px] text-teal-600 dark:text-teal-400/60 uppercase tracking-wide font-semibold mb-0.5">
              Sélection active
            </div>
            <div className="text-[10px] text-teal-700 dark:text-teal-300/90 leading-snug">
              {selectionSummary}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Rechercher D700, obturation..."
            className="w-full pl-8 pr-3 py-1.5 text-[11px] bg-background border border-border rounded-lg
              text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-teal-500/40
              focus:border-teal-600/50 transition-all"
          />
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 custom-scrollbar">
        {/* Clinical Intentions (smart grouped buttons) */}
        {hasSelection && clinicalButtons.length > 0 && !searchQuery && (
          <div className="mb-3">
            <div className="flex items-center gap-2 px-1 py-1.5">
              <svg className="h-3.5 w-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
              </svg>
              <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400/90 uppercase tracking-wider">
                Actions cliniques suggérées
              </span>
            </div>

            <div className="space-y-1.5">
              {clinicalButtons.map(({ intention, bestAct, allActs }) =>
                renderClinicalButton(intention, bestAct, allActs)
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-2 mt-3 mb-1">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700/50" />
              <button
                onClick={() => setShowFullCatalog(!showFullCatalog)}
                className="text-[10px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 transition-colors"
              >
                {showFullCatalog ? 'Masquer catalogue' : 'Voir tout le catalogue'}
              </button>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700/50" />
            </div>
          </div>
        )}

        {/* Full Catalog (always shown if no selection, or toggled) */}
        {(!hasSelection || showFullCatalog || searchQuery) && (
          <div className="space-y-1">
            {filteredCategories.map(cat => {
              const isExpanded = expandedCategory === cat.id || !!searchQuery;
              const actCount = cat.acts?.length ?? 0;

              return (
                <div key={cat.id} className="rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    aria-expanded={isExpanded}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors rounded-lg"
                  >
                    {cat.icon_image ? (
                      <img src={cat.icon_image} alt="" className="w-5 h-5 rounded object-contain shrink-0" />
                    ) : (
                      <span
                        className="w-2 h-2 rounded-sm shrink-0"
                        style={{ backgroundColor: cat.color || '#64748b' }}
                      />
                    )}
                    <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300 flex-1 truncate">
                      {cat.name}
                    </span>
                    <span className="text-[10px] text-slate-500 tabular-nums">{actCount}</span>
                    <svg
                      className={`h-3 w-3 text-slate-500 transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="pl-2 space-y-0.5 pb-1">
                      {(cat.acts || []).map(act => renderActItem(act))}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredCategories.length === 0 && searchQuery && (
              <div className="text-center py-8">
                <p className="text-[11px] text-slate-500">
                  Aucun acte trouvé pour "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
